import * as React from 'react';
import { useGlobalContext } from '../state/GlobalStateProvider';
import {
    Add24Regular,
    Delete24Regular
} from "@fluentui/react-icons";
import { Checkbox, makeStyles, shorthands, useId, InfoLabel, Input, ToolbarButton, CheckboxOnChangeData, Spinner, TableCellLayout, TableColumnDefinition, createTableColumn, TableRowId, InputOnChangeData, Field } from '@fluentui/react-components';
import { FluentDialog } from './FluentDialog';
import { formInputInlineStyle, formInputStyle, formStyle, infoLabel } from '../styles/GlobalStyles';
import { GenericGrid } from './GenericGrid';
import { TrustedDomain, UserRecord } from '../dto/User';
import { v4 as uuidv4 } from 'uuid';
import { ensureSuccess, handleError } from '../util/ErrorHandling';

const rowSelector = (item: TrustedDomain) => item.guid;

const useStyles = makeStyles({
    root: {
        fontWeight: "800",
        width: "80%",
        ...shorthands.margin("0", "auto")
    },
    activeNavLink: {
        ...shorthands.borderBottom("3px", "solid", "white"),
        ...shorthands.padding("5px")
    },
    pendingNavLink: {
        backgroundColor: "lightgray",
        ...shorthands.padding("5px")
    },
    userIcon: {
        marginLeft: "20px",
        cursor: "pointer",
        display: "inline-block"
    },
    form: formStyle,
    formInput: formInputStyle,
    formInputInline: formInputInlineStyle,
    infoLabel: infoLabel
});

export interface UserSettingsDialogProps
{
    userSettingsDialogVisible: boolean;
    hideUserSettingsDialog: () => void;
}

export const UserSettingsDialog: React.FC<UserSettingsDialogProps> = ({ userSettingsDialogVisible, hideUserSettingsDialog }) =>
{
    const styles = useStyles();

    const [globalState, globalDispatch] = useGlobalContext();
    const [userRecord, setUserRecord] = React.useState({} as UserRecord);

    const [isSaving, setIsSaving] = React.useState(false);
    const [selectedTrustedDomains, setSelectedTrustedDomains] = React.useState<TableRowId[]>([]);
    const [isAddingTrustedDomain, setIsAddingTrustedDomain] = React.useState(false);
    const [trustedDomainId, setTrustedDomainId] = React.useState("");
    const [trustedDomainName, setTrustedDomainName] = React.useState("");

    const publicVisibleCheckBoxId = useId("public-visibility-checkbox");

    const columns: TableColumnDefinition<TrustedDomain>[] = React.useMemo(() => [
        createTableColumn<TrustedDomain>({
        columnId: "Domain",
        compare: (a, b) => {
            return a.name.localeCompare(b.name);
        },
        renderHeaderCell: () => {
            return "Domain";
        },
        renderCell: (item) => {
            return (
            <TableCellLayout truncate>
                {item.name}
            </TableCellLayout>
            );
        },
        })
    ], []);

    const cancelDialog = React.useCallback(() =>
    {
        setTrustedDomainId("");
        setTrustedDomainName("");
        setIsAddingTrustedDomain(false);
    }, []);

    const submitDialog = React.useCallback(() =>
    {
        if (trustedDomainId)
        {
            setUserRecord({
                ...userRecord,
                settings:
                {
                    ...(userRecord.settings ?? {}),
                    visibilitySettings:
                    {
                        ...(userRecord.settings?.visibilitySettings ?? {}),
                        trustedDomains: [
                            ...(userRecord.settings?.visibilitySettings?.trustedDomains ?? []).map(e => e.guid === trustedDomainId ? ({...e, name: trustedDomainName }) : e)
                        ]
                    }
                }
            });
        }
        else {
            const newEnv: TrustedDomain = {
                guid: uuidv4(),
                name: trustedDomainName
            };

            setUserRecord({
                ...userRecord,
                settings:
                {
                    ...(userRecord.settings ?? {}),
                    visibilitySettings:
                    {
                        ...(userRecord.settings?.visibilitySettings ?? {}),
                        trustedDomains: [ ...(userRecord.settings?.visibilitySettings?.trustedDomains ?? []), newEnv ]
                    }
                }
            });
        }
        cancelDialog();
    }, [cancelDialog, trustedDomainName, trustedDomainId, userRecord]);

    const editTrustedDomain = React.useCallback((domainId: TableRowId) =>
    {
        const env = userRecord.settings?.visibilitySettings?.trustedDomains?.find(e => e.guid === domainId.toString());

        if (env)
        {
            setTrustedDomainId(env.guid);
            setTrustedDomainName(env.name);
            setIsAddingTrustedDomain(true);
        }
    }, [userRecord.settings?.visibilitySettings?.trustedDomains]);

    const onTrustedDomainSelection = React.useCallback((selection: TableRowId[]) =>
    {
        setSelectedTrustedDomains(selection);
    }, []);

    const onTrustedDomainDeletion = React.useCallback(() =>
    {
        setUserRecord({
            ...userRecord,
            settings:
            {
                ...(userRecord.settings ?? {}),
                visibilitySettings:
                {
                    publiclyVisible: userRecord.settings?.visibilitySettings?.publiclyVisible ?? false,
                    trustedDomains: [
                        ...(userRecord.settings?.visibilitySettings?.trustedDomains ?? []).filter(e => !selectedTrustedDomains.includes(e.guid))
                    ]
                }
            }
        });
    }, [userRecord, selectedTrustedDomains]);

    const onTrustedDomainNameChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) =>
    {
        setTrustedDomainName(data.value)
    }, []);

    const showTrustedDomainDialog = React.useCallback(() => setIsAddingTrustedDomain(true), []);

    const submitUserSettings = React.useCallback(async () =>
    {
        try
        {
            const response = await fetch(`/api/users`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json"
                },        
                body: JSON.stringify(userRecord)
            });

            await ensureSuccess(response);

            const user = await response.json();
            globalDispatch({ type: "setUser", payload: user });

            hideUserSettingsDialog();
        }
        catch (e) {
            handleError(e as Error, globalDispatch);
        }
        finally {
            setIsSaving(false);
        }
    }, [globalDispatch, hideUserSettingsDialog, userRecord]);

    const onPublicVisibleChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: CheckboxOnChangeData) =>
    {
        if (!data.checked)
        {
            setUserRecord({
                ...userRecord,
                settings:
                {
                    ...(userRecord.settings ?? {}),
                    visibilitySettings:
                    {
                        publiclyVisible: false,
                        trustedDomains: []
                    }
                }
            });
        }
        else
        {
            setUserRecord({
                ...userRecord,
                settings:
                {
                    ...(userRecord.settings ?? {}),
                    visibilitySettings:
                    {
                        publiclyVisible: true,
                        trustedDomains: []
                    }
                }
            });
        }
    }, [userRecord]);

    React.useEffect(() =>
    {
        if (globalState.user)
        {
            setUserRecord(globalState.user);
        }
    }, [globalState.user, userSettingsDialogVisible]);

    return (
        <FluentDialog title="Settings" submitLabel='Save' cancelLabel='Discard' open={userSettingsDialogVisible} onSubmit={submitUserSettings} onCancel={hideUserSettingsDialog}>

            <FluentDialog title={`${ trustedDomainId ? "Edit" : "Add"} Trusted Domain`} open={isAddingTrustedDomain} submitDisabled={!trustedDomainName} onSubmit={submitDialog} onCancel={cancelDialog}>
                <div className={styles.form}>
                    <Field
                        label={
                            <InfoLabel
                                info="Enter the domain which should be allowed to find your user when adding to their publisher. Set 'microsoft.com' for example if a user 'john.doe@microsoft.com' should be able to add you to publishers."
                                required
                            >
                                Domain
                            </InfoLabel>
                        }
                    >
                        <Input type="text" value={trustedDomainName ?? ""} onChange={onTrustedDomainNameChange} />
                    </Field>
                </div>
            </FluentDialog>

            { (!userRecord.guid || isSaving) && 
                <div className={styles.form}>
                    <Spinner />
                </div>
            }
            { (!!userRecord.guid && !isSaving) &&
                <div className={styles.form}>
                    <Field
                        label={
                            <InfoLabel size="medium" required className={styles.infoLabel} info="Your account's email address">
                                E-Mail
                            </InfoLabel>
                        }
                    >
                        <Input type="text" value={userRecord.emailAddress} disabled />
                    </Field>

                    <div className={styles.formInputInline}>
                        <InfoLabel htmlFor={publicVisibleCheckBoxId} size="medium" className={styles.infoLabel} info="This is a requirement for being able to be added to other publishers. Your e-mail address will be visible to users from your trusted domains list and to all users inside publishers to which you are added. You understand and agree that once you are added to an publisher, all of its members can see your e-mail address.">
                            Make my profile publicly visible. I understand and accept that my e-mail address will be visible to other users.
                        </InfoLabel>
                        <Checkbox id={publicVisibleCheckBoxId} checked={userRecord.settings?.visibilitySettings?.publiclyVisible ?? false} onChange={onPublicVisibleChange} />
                    </div>

                    { userRecord.settings?.visibilitySettings?.publiclyVisible &&
                        <div style={{ borderRadius: "5px", width: "100%", height: "100%", marginTop: "20px" }}>
                            <InfoLabel size="medium" className={styles.infoLabel} info="Enter the domains which should be allowed to find your user when adding to their publisher. Set 'microsoft.com' for example if a user 'john.doe@microsoft.com' should be able to add you to publishers. If you don't set any trusted domain, no one can add you, even if your profile is publicly visible.">
                                Trusted Domains
                            </InfoLabel>

                            <GenericGrid
                                onRowClick={editTrustedDomain}
                                onSelectionChange={onTrustedDomainSelection}
                                columns={columns}
                                items={userRecord.settings?.visibilitySettings?.trustedDomains ?? []}
                                rowSelector={rowSelector}
                                actionButtons={
                                    <>
                                        <ToolbarButton
                                            aria-label="Add new record"
                                            appearance="primary"
                                            icon={<Add24Regular />}
                                            onClick={showTrustedDomainDialog}
                                        />
                                        <ToolbarButton
                                            aria-label="Delete record"
                                            icon={<Delete24Regular />}
                                            onClick={onTrustedDomainDeletion}
                                        />
                                    </>
                                }
                            />
                        </div>
                    }
                </div>
            }
        </FluentDialog>
    );
};